// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Grid, Loader, ScrollArea, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import type { DatePickerProps } from '@mantine/dates';
import { DatePicker } from '@mantine/dates';
import type { AppointmentTypeDefinition, AvailableTimeSlot } from '@polarissleep/scheduling';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAvailableSlots } from '../../hooks/useAvailableSlots';
import type { PatientAppointmentCandidate } from '../../scheduling-next/types';
import { useAvailableAppointments } from '../../scheduling-next/useAvailableAppointments';
import { formatAppointmentTime } from '../../utils/appointment-formatting';
import { getEarliestPatientBookableStart } from '../../utils/patient-scheduling-notice';

interface LegacySlotPickerProps {
  engine?: 'legacy';
  /** Pre-resolved appointment type definition */
  appointmentType: AppointmentTypeDefinition;
  practitionerId?: string;
  selectedSlot: AvailableTimeSlot | undefined;
  onSlotSelected: (slot: AvailableTimeSlot) => void;
}

interface V2SlotPickerProps {
  engine: 'medplum-v2';
  /** Pre-resolved appointment type definition */
  appointmentType: AppointmentTypeDefinition;
  practitionerId?: string;
  selectedSlot: PatientAppointmentCandidate | undefined;
  onSlotSelected: (slot: PatientAppointmentCandidate) => void;
  onSearchStateChange?: (state: { ready: boolean; error: Error | null }) => void;
}

type SlotPickerProps = LegacySlotPickerProps | V2SlotPickerProps;

/**
 * Side-by-side calendar and time slot picker for patient self-scheduling.
 * Left: inline month calendar. Right: scrollable list of available time slot pills.
 * @param root0 - Component props
 * @param root0.appointmentType - The pre-resolved appointment type definition
 * @param root0.selectedSlot - The currently selected slot (if any)
 * @param root0.onSlotSelected - Callback when a time slot is selected
 * @returns The slot picker component
 */
export function SlotPicker({ ...props }: SlotPickerProps): JSX.Element {
  if (props.engine === 'medplum-v2') {
    return <V2SlotPicker {...props} />;
  }

  return <LegacySlotPicker {...props} />;
}

function LegacySlotPicker({
  appointmentType,
  practitionerId,
  selectedSlot,
  onSlotSelected,
}: LegacySlotPickerProps): JSX.Element {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(new Date());

  // Fetch slots for the visible month to highlight dates with availability
  const monthStart = toISODateString(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1));
  const monthEnd = toISODateString(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1));
  const {
    slots: monthSlots,
    loading,
    error,
  } = useAvailableSlots({
    appointmentType,
    practitionerId,
    startDate: monthStart,
    endDate: monthEnd,
  });

  const slots = useMemo(() => {
    if (!selectedDate) {
      return [];
    }

    return monthSlots.filter((slot) => isSameLocalDate(new Date(slot.start), selectedDate));
  }, [monthSlots, selectedDate]);

  // Build a set of date strings (YYYY-MM-DD) that have availability
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const slot of monthSlots) {
      const d = new Date(slot.start);
      dates.add(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      );
    }
    return dates;
  }, [monthSlots]);

  const minDate = getEarliestPatientBookableStart();

  // Highlight dates: selected date gets brand color, available dates get light tint
  const getDayProps: DatePickerProps['getDayProps'] = useCallback(
    (date: string) => {
      const d = new Date(date + 'T00:00:00');
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      // Selected date: primary brand color
      const isSelected =
        selectedDate &&
        d.getFullYear() === selectedDate.getFullYear() &&
        d.getMonth() === selectedDate.getMonth() &&
        d.getDate() === selectedDate.getDate();

      if (isSelected) {
        return {
          style: {
            backgroundColor: 'var(--mantine-color-polaris-5)',
            color: 'white',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        };
      }

      // Available dates: light tint
      if (availableDates.has(key)) {
        return {
          style: {
            backgroundColor: 'var(--mantine-color-polaris-0)',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        };
      }

      return {};
    },
    [availableDates, selectedDate]
  );

  const formattedSelectedDate = selectedDate
    ? selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <Stack gap="md">
      <Title order={3}>Available Appointments</Title>
      <Text size="sm" c="dimmed">
        Choose a date and time.
      </Text>

      <Grid gutter="xl">
        {/* Left: Calendar */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Box
            p="md"
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 'var(--mantine-radius-md)',
            }}
          >
            <DatePicker
              value={selectedDate}
              onChange={(value: string | Date | null) => {
                if (!value) {
                  setSelectedDate(null);
                } else if (typeof value === 'string') {
                  const [year, month, day] = value.split('-').map(Number);
                  setSelectedDate(new Date(year, month - 1, day));
                } else {
                  setSelectedDate(new Date(value.getFullYear(), value.getMonth(), value.getDate()));
                }
              }}
              onDateChange={(value: string) => {
                const [year, month] = value.split('-').map(Number);
                setVisibleMonth(new Date(year, month - 1, 1));
              }}
              getDayProps={getDayProps}
              minDate={minDate}
              size="lg"
              styles={{
                calendarHeader: { maxWidth: '100%' },
                month: { width: '100%' },
                day: {
                  width: '100%',
                  height: 48,
                  fontSize: 'var(--mantine-font-size-md)',
                  borderRadius: 'var(--mantine-radius-sm)',
                },
              }}
            />
          </Box>
        </Grid.Col>

        {/* Right: Time Slots */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          {!selectedDate && (
            <Stack align="center" justify="center" h="100%" py="xl">
              <Text c="dimmed" size="sm" ta="center">
                Select a date to see available times.
              </Text>
            </Stack>
          )}

          {selectedDate && (
            <Stack gap="sm">
              <Text size="sm" c="dimmed" ta="center">
                Available times for
              </Text>
              <Text fw={600} ta="center" size="md">
                {formattedSelectedDate}
              </Text>

              {loading && (
                <Stack align="center" py="xl">
                  <Loader size="sm" />
                </Stack>
              )}

              {error && (
                <Text c="red" size="sm" ta="center">
                  {error.message}
                </Text>
              )}

              {!loading && slots.length === 0 && (
                <Text c="dimmed" size="sm" ta="center" py="xl">
                  No available times on this date. Try another date.
                </Text>
              )}

              {!loading && slots.length > 0 && (
                <ScrollArea h={400} offsetScrollbars>
                  <Stack gap="xs">
                    {slots.map((slot) => {
                      const isSelected = selectedSlot?.slotId === slot.slotId;
                      return (
                        <UnstyledButton
                          key={slot.slotId}
                          onClick={() => onSlotSelected(slot)}
                          py="sm"
                          px="md"
                          style={{
                            borderRadius: 'var(--mantine-radius-md)',
                            border: `2px solid ${isSelected ? 'var(--mantine-color-polaris-5)' : 'var(--mantine-color-polaris-1)'}`,
                            backgroundColor: isSelected
                              ? 'var(--mantine-color-polaris-5)'
                              : 'var(--mantine-color-polaris-0)',
                            color: isSelected ? 'white' : 'inherit',
                            textAlign: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <Text size="sm" fw={500}>
                            {formatAppointmentTime(slot.start, slot.end)}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function V2SlotPicker({
  appointmentType,
  practitionerId,
  selectedSlot,
  onSlotSelected,
  onSearchStateChange,
}: V2SlotPickerProps): JSX.Element {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(new Date());
  const monthStart = toISODateString(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1));
  const monthEnd = toISODateString(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1));
  const {
    appointments: monthAppointments,
    loading,
    error,
    ready,
  } = useAvailableAppointments({ appointmentType, practitionerId, startDate: monthStart, endDate: monthEnd });
  const appointments = useMemo(() => {
    if (!selectedDate) {
      return [];
    }
    return monthAppointments.filter((candidate) =>
      isSameLocalDate(new Date(candidate.appointment.start ?? ''), selectedDate)
    );
  }, [monthAppointments, selectedDate]);
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const candidate of monthAppointments) {
      const date = new Date(candidate.appointment.start ?? '');
      dates.add(
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      );
    }
    return dates;
  }, [monthAppointments]);
  const maxDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 60);
    return date;
  }, []);
  const getDayProps: DatePickerProps['getDayProps'] = useCallback(
    (date: string) => {
      const day = new Date(`${date}T00:00:00`);
      const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
      const isSelected = selectedDate && isSameLocalDate(day, selectedDate);
      if (isSelected) {
        return {
          'data-date': date,
          style: {
            backgroundColor: 'var(--mantine-color-polaris-5)',
            color: 'white',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        };
      }
      if (availableDates.has(key)) {
        return {
          'data-date': date,
          style: {
            backgroundColor: 'var(--mantine-color-polaris-0)',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        };
      }
      return { 'data-date': date };
    },
    [availableDates, selectedDate]
  );
  const formattedSelectedDate = selectedDate
    ? selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  const searchState = getV2SearchState(loading, error, ready);
  const monthKey = `${visibleMonth.getFullYear()}-${String(visibleMonth.getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    onSearchStateChange?.({ ready, error });
  }, [error, onSearchStateChange, ready]);

  return (
    <Stack gap="md" data-v2-scheduling-search={searchState} data-v2-scheduling-month={monthKey}>
      <Title order={3}>Available Appointments</Title>
      <Text size="sm" c="dimmed">
        Choose a date and time.
      </Text>
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Box
            p="md"
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 'var(--mantine-radius-md)',
            }}
          >
            <DatePicker
              value={selectedDate}
              onChange={(value: string | Date | null) => {
                if (!value) {
                  setSelectedDate(null);
                } else if (typeof value === 'string') {
                  const [year, month, day] = value.split('-').map(Number);
                  setSelectedDate(new Date(year, month - 1, day));
                } else {
                  setSelectedDate(new Date(value.getFullYear(), value.getMonth(), value.getDate()));
                }
              }}
              onDateChange={(value: string) => {
                const [year, month] = value.split('-').map(Number);
                setVisibleMonth(new Date(year, month - 1, 1));
              }}
              getDayProps={getDayProps}
              minDate={getEarliestPatientBookableStart()}
              maxDate={maxDate}
              nextLabel="Next month"
              previousLabel="Previous month"
              size="lg"
              styles={{
                calendarHeader: { maxWidth: '100%' },
                month: { width: '100%' },
                day: {
                  width: '100%',
                  height: 48,
                  fontSize: 'var(--mantine-font-size-md)',
                  borderRadius: 'var(--mantine-radius-sm)',
                },
              }}
            />
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          {!selectedDate && (
            <Stack align="center" justify="center" h="100%" py="xl">
              <Text c="dimmed" size="sm" ta="center">
                Select a date to see available times.
              </Text>
            </Stack>
          )}
          {selectedDate && (
            <Stack gap="sm">
              <Text size="sm" c="dimmed" ta="center">
                Available times for
              </Text>
              <Text fw={600} ta="center" size="md">
                {formattedSelectedDate}
              </Text>
              {loading && (
                <Stack align="center" py="xl">
                  <Loader size="sm" />
                </Stack>
              )}
              {error && (
                <Text c="red" size="sm" ta="center">
                  {error.message}
                </Text>
              )}
              {!loading && appointments.length === 0 && (
                <Text c="dimmed" size="sm" ta="center" py="xl">
                  No available times on this date. Try another date.
                </Text>
              )}
              {!loading && appointments.length > 0 && (
                <ScrollArea h={400} offsetScrollbars>
                  <Stack gap="xs">
                    {appointments.map((candidate) => {
                      const isSelected = selectedSlot?.key === candidate.key;
                      return (
                        <UnstyledButton
                          key={candidate.key}
                          data-v2-candidate="true"
                          disabled={!ready || Boolean(error)}
                          onClick={() => {
                            if (ready && !error) {
                              onSlotSelected(candidate);
                            }
                          }}
                          py="sm"
                          px="md"
                          style={{
                            borderRadius: 'var(--mantine-radius-md)',
                            border: `2px solid ${isSelected ? 'var(--mantine-color-polaris-5)' : 'var(--mantine-color-polaris-1)'}`,
                            backgroundColor: isSelected
                              ? 'var(--mantine-color-polaris-5)'
                              : 'var(--mantine-color-polaris-0)',
                            color: isSelected ? 'white' : 'inherit',
                            textAlign: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <Text size="sm" fw={500}>
                            {formatAppointmentTime(candidate.appointment.start, candidate.appointment.end)}
                          </Text>
                          <Text size="xs" c={isSelected ? 'white' : 'dimmed'}>
                            {candidate.providerName}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

/**
 * Converts a Date to an ISO datetime string at local midnight (then expressed in UTC).
 * Using local midnight ensures the FHIR date query boundaries align with the
 * patient's local day, not UTC midnight which can fall on a different local date.
 * @param date - The date to convert
 * @returns ISO datetime string (UTC representation of local midnight)
 */
function toISODateString(date: Date): string {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
}

function isSameLocalDate(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function getV2SearchState(loading: boolean, error: Error | null, ready: boolean): 'loading' | 'error' | 'ready' {
  if (error) {
    return 'error';
  }
  if (loading || !ready) {
    return 'loading';
  }
  return 'ready';
}
